import { useLogout, useSession, useSwitchProfile } from './api/hooks.js';
import { CreatorDashboard } from './ui/CreatorDashboard.js';
import { Login } from './ui/Login.js';
import { Logo, Wordmark } from './ui/Logo.js';
import { MemberDashboard } from './ui/MemberDashboard.js';
import { ErrorNote } from './ui/shared.js';

export function App() {
  const session = useSession();

  if (session.isPending) {
    return (
      <main className="auth">
        <p className="empty">Checking your session…</p>
      </main>
    );
  }

  // Signed out is a value, not an error — see useSession.
  if (session.data === null) return <Login />;

  if (session.isError) {
    return (
      <main className="auth">
        <div className="auth__card">
          <Logo size={44} title="KickBack Pro" />
          <h1 className="auth__title">KickBack Pro</h1>
          <ErrorNote error={session.error} />
          <p className="field__hint">
            The API may not be running. Check that <code>npm run dev:api</code> is up.
          </p>
        </div>
      </main>
    );
  }

  const isCreator = session.data!.principal.kind === 'creator';

  return (
    <>
      <Header />
      <main className="page">{isCreator ? <CreatorDashboard /> : <MemberDashboard />}</main>
    </>
  );
}

function Header() {
  const session = useSession();
  const switchProfile = useSwitchProfile();
  const logout = useLogout();

  // Null (signed out) as well as undefined (still loading) — the header has
  // nothing to show in either case.
  const data = session.data;
  if (data == null) return null;

  // Only worth showing when the identity actually owns more than one profile.
  const canSwitch = data.availablePrincipals.length > 1;

  return (
    <header className="topbar">
      <div className="topbar__inner">
        <div className="topbar__brand">
          <Wordmark />
          <span className="topbar__role">
            {data.principal.kind === 'creator' ? 'Creator' : 'Fan'}
          </span>
        </div>

        {canSwitch && (
          <div className="toggle" role="group" aria-label="Switch profile">
            {data.availablePrincipals.map((principal) => (
              <button
                key={principal.id}
                type="button"
                className={`toggle__option${
                  principal.id === data.principal.id ? ' toggle__option--on' : ''
                }`}
                onClick={() => switchProfile.mutate(principal.id)}
                disabled={switchProfile.isPending}
              >
                {principal.kind === 'creator' ? 'Creator' : 'Fan'}
              </button>
            ))}
          </div>
        )}

        <div className="topbar__right">
          <span className="topbar__email">{data.email}</span>
          <button
            className="button button--quiet"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
